import { type FormEvent, useEffect, useState } from "react";

interface LoginFormProps {
  onLogin: (username: string, password: string) => void | Promise<void>;
  isLoading?: boolean;
  error?: string | null;
  serverUrl?: string;
  onServerUrlChange?: (url: string) => void;
}

export function LoginForm({
  onLogin,
  isLoading,
  error,
  serverUrl,
  onServerUrlChange,
}: LoginFormProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showServer, setShowServer] = useState(false);
  const [urlDraft, setUrlDraft] = useState(serverUrl ?? "");

  // Keep draft in sync if serverUrl prop changes externally
  useEffect(() => {
    if (serverUrl != null) setUrlDraft(serverUrl);
  }, [serverUrl]);

  const handleSubmit = (e?: FormEvent) => {
    e?.preventDefault();
    if (!username || !password || isLoading) return;
    onLogin(username, password);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--panel-spacing-xl)",
        height: "100%",
        minHeight: 300,
      }}
    >
      <div
        style={{
          fontSize: "var(--panel-font-size-xl)",
          fontWeight: 700,
          marginBottom: "var(--panel-spacing-xs)",
        }}
      >
        HOPS Artwork
      </div>
      <div
        style={{
          fontSize: "var(--panel-font-size-sm)",
          color: "var(--panel-text-muted)",
          marginBottom: "var(--panel-spacing-xl)",
        }}
      >
        Sign in to access your queue
      </div>

      <form onSubmit={handleSubmit} style={{ width: "100%", maxWidth: 260 }}>
        <div className="hops-field">
          <label className="hops-label" htmlFor="hops-username">
            Username
          </label>
          <input
            id="hops-username"
            className="hops-input"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Enter username"
            autoComplete="username"
            autoFocus
            data-testid="panel-username-input"
          />
        </div>

        <div className="hops-field">
          <label className="hops-label" htmlFor="hops-password">
            Password
          </label>
          <input
            id="hops-password"
            className="hops-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            autoComplete="current-password"
            data-testid="panel-password-input"
          />
        </div>

        {error && (
          <div
            style={{
              color: "var(--panel-danger)",
              fontSize: "var(--panel-font-size-sm)",
              marginBottom: "var(--panel-spacing-md)",
            }}
            data-testid="panel-login-error"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          className="hops-btn hops-btn-primary hops-btn-full"
          disabled={!username || !password || isLoading}
          onClick={() => handleSubmit()}
          data-testid="panel-login-button"
        >
          {isLoading ? "Signing in..." : "Sign In"}
        </button>
      </form>

      {serverUrl != null && onServerUrlChange && (
        <div
          style={{
            marginTop: "var(--panel-spacing-lg)",
            width: "100%",
            maxWidth: 260,
          }}
        >
          <button
            type="button"
            onClick={() => setShowServer((v) => !v)}
            data-testid="panel-server-url-toggle"
            style={{
              background: "none",
              border: "none",
              color: "var(--panel-text-muted)",
              fontSize: "var(--panel-font-size-xs)",
              cursor: "pointer",
              padding: 0,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <span style={{ fontSize: 8 }}>
              {showServer ? "\u25BC" : "\u25B6"}
            </span>
            Server
          </button>

          {showServer && (
            <div style={{ marginTop: "var(--panel-spacing-xs)" }}>
              <input
                className="hops-input"
                type="url"
                value={urlDraft}
                onChange={(e) => setUrlDraft(e.target.value)}
                onBlur={() => {
                  if (urlDraft !== serverUrl) {
                    onServerUrlChange(urlDraft);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (urlDraft !== serverUrl) {
                      onServerUrlChange(urlDraft);
                    }
                  }
                }}
                placeholder="https://hops.next.hodgesbadge.com"
                data-testid="panel-server-url-input"
                style={{ fontSize: "var(--panel-font-size-sm)" }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
